// Pure aggregation helpers for the Statistics page.
// All functions take an array of reservation objects and return plain data
// ready to feed into KPI cards, Recharts, or tables.

const guestsOf = (r) => Number(r.total_guest) || 0;

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// --- KPIs -----------------------------------------------------------------

export const computeKpis = (reservations) => {
  const total = reservations.length;
  const totalGuests = reservations.reduce((s, r) => s + guestsOf(r), 0);
  const canceled = reservations.filter((r) => r.state === 'canceled').length;
  const completed = reservations.filter((r) => r.state === 'completed').length;

  return {
    total,
    totalGuests,
    avgPartySize: total > 0 ? totalGuests / total : 0,
    cancellationRate: total > 0 ? (canceled / total) * 100 : 0,
    completionRate: total > 0 ? (completed / total) * 100 : 0,
  };
};

// --- Time trend -----------------------------------------------------------

// Groups reservations by booking_date, returning an array sorted ascending by
// date with { date, reservas, pessoas }.
export const groupByDate = (reservations) => {
  const map = new Map();
  reservations.forEach((r) => {
    if (!r.booking_date) return;
    const entry = map.get(r.booking_date) || { date: r.booking_date, reservas: 0, pessoas: 0 };
    entry.reservas += 1;
    entry.pessoas += guestsOf(r);
    map.set(r.booking_date, entry);
  });
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
};

// --- Distributions --------------------------------------------------------

export const groupByBranch = (reservations) => {
  const map = new Map();
  reservations.forEach((r) => {
    const key = r.branch || 'Sem restaurante';
    map.set(key, (map.get(key) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

export const groupByStatus = (reservations) => {
  const map = new Map();
  reservations.forEach((r) => {
    const key = r.state || 'pending';
    map.set(key, (map.get(key) || 0) + 1);
  });
  return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
};

export const groupByWeekday = (reservations) => {
  const counts = new Array(7).fill(0);
  reservations.forEach((r) => {
    if (!r.booking_date) return;
    // Parse as local date to keep the weekday aligned with the booking day.
    const [y, m, d] = r.booking_date.split('-').map(Number);
    if (!y || !m || !d) return;
    const weekday = new Date(y, m - 1, d).getDay();
    counts[weekday] += 1;
  });
  // Present Monday-first, which reads more naturally for a restaurant week.
  const order = [1, 2, 3, 4, 5, 6, 0];
  return order.map((idx) => ({ name: WEEKDAY_LABELS[idx], value: counts[idx] }));
};

export const groupByHour = (reservations) => {
  const map = new Map();
  reservations.forEach((r) => {
    if (!r.from_time) return;
    const hour = r.from_time.split(':')[0];
    if (hour === undefined) return;
    const label = `${hour}h`;
    map.set(label, (map.get(label) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([name, value]) => ({ name, value, hour: parseInt(name, 10) }))
    .sort((a, b) => a.hour - b.hour);
};

export const groupByPartySize = (reservations) => {
  const map = new Map();
  reservations.forEach((r) => {
    const size = guestsOf(r);
    if (size <= 0) return;
    map.set(size, (map.get(size) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([size, value]) => ({ name: `${size}`, value, size }))
    .sort((a, b) => a.size - b.size);
};

// --- Recurring customers --------------------------------------------------

// Groups reservations by phone (falls back to email, then name), returning the
// top `limit` customers by reservation count.
export const topCustomers = (reservations, limit = 10) => {
  const map = new Map();
  reservations.forEach((r) => {
    const key = r.phone || r.email || r.name || 'Desconhecido';
    const entry = map.get(key) || {
      key,
      name: r.name || '—',
      contact: r.phone || r.email || '—',
      reservas: 0,
      pessoas: 0,
    };
    entry.reservas += 1;
    entry.pessoas += guestsOf(r);
    // Keep the most complete name/contact we encounter.
    if (r.name) entry.name = r.name;
    map.set(key, entry);
  });
  return Array.from(map.values())
    .sort((a, b) => b.reservas - a.reservas || b.pessoas - a.pessoas)
    .slice(0, limit);
};
